import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Group,
  JsonInput,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import {
  IAlertRule,
  IAlertRuleConfiguration,
  ICreateAlertRuleConfigurationRequest,
  IUpdateAlertRuleConfigurationRequest,
} from '../../../../global/entities/Alert/Alert.ts';
import {
  useCreateAlertRuleConfiguration,
  useUpdateAlertRuleConfiguration,
} from '../../../../global/entities/Alert/mutations';
import { IHealthcareFacility } from '../../../../global/entities/healthcareFacility/HealthcareFacility.ts';
import { IJournal } from '../../../../global/entities/Journal/Journal.ts';
import { IQuestionnaires } from '../../../../global/entities/Questionnaires/Questionnaires.ts';
import { IPhysiologicalType } from '../../../../global/entities/PhysiologicalData/PhysiologicalData.ts';
import { useGetCategoryConfigs } from '../../../../global/entities/Questionnaires/queries/useGetCategoryConfigs.tsx';
import { useJournal } from '../../../../global/entities/Journal/queries/useJournals.ts';
import QuestionnaireConditionFields from './QuestionnaireConditionFields.tsx';
import JournalConditionFields from './JournalConditionFields.tsx';
import PhysiologicalConditionFields from './PhysiologicalConditionFields.tsx';
import JournalInactivityConditionFields from './JournalInactivityConditionFields.tsx';
import QuestionnaireOverdueConditionFields from './QuestionnaireOverdueConditionFields.tsx';
import { QUESTION_TYPE_IDS } from '../../../PatientDashboard/PatientJournal/QuestionTypes.ts';
import {
  ConfigFormValues,
  defaultConfigValues,
  getQuestionType,
  getSeverityOptions,
  TREND_TYPE_SUDDEN_INCREASE,
} from './alertConfigurationConstants.ts';
import { useGetHealthcareProfessionalsByFacility } from '../../../../global/entities/healthcareProfessional/queries/useGetHealthcareProfessionalsByFacility.tsx';
import Utils from '../../../../global/helpers/utils.ts';

interface Props {
  opened: boolean;
  onClose: () => void;
  rule: IAlertRule;
  editingConfig: IAlertRuleConfiguration | null;
  ruleId: number;
  facilities: IHealthcareFacility[];
  journals: IJournal[];
  questionnaires: IQuestionnaires[];
  physiologicalTypes: IPhysiologicalType[];
}

const AlertConfigurationModal: React.FC<Props> = ({
  opened,
  onClose,
  rule,
  editingConfig,
  ruleId,
  facilities,
  journals,
  questionnaires,
  physiologicalTypes,
}) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const createConfiguration = useCreateAlertRuleConfiguration();
  const updateConfiguration = useUpdateAlertRuleConfiguration();

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    number | null
  >(null);
  const [selectedJournalId, setSelectedJournalId] = useState<number | null>(
    null,
  );
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | null>(
    null,
  );
  const [escalationDelayUnit, setEscalationDelayUnit] = useState<
    'minutes' | 'hours' | 'days'
  >('days');
  const [level2EscalationDelayUnit, setLevel2EscalationDelayUnit] = useState<
    'minutes' | 'hours' | 'days'
  >('days');

  const { data: facilityHcps } =
    useGetHealthcareProfessionalsByFacility(selectedFacilityId);

  const isQuestionnaireOverdueRule = rule.code === 'QUESTIONNAIRE_OVERDUE';
  const isQuestionnaireAvailableRule = rule.code === 'QUESTIONNAIRE_AVAILABLE';
  // A questionnaire-scored rule (category/threshold). The overdue and availability
  // reminders share sourceType 'Questionnaire' (so the questionnaire scope dropdown
  // populates) but are NOT score rules, so exclude them here to skip the category
  // UI/validation.
  const isQuestionnaireRule =
    rule.sourceType === 'Questionnaire' &&
    !isQuestionnaireOverdueRule &&
    !isQuestionnaireAvailableRule;
  const isJournalResponseRule = rule.code === 'JOURNAL_RESPONSE';
  const isJournalInactivityRule = rule.code === 'JOURNAL_INACTIVITY';
  const isPhysiologicalRule = rule.sourceType === 'Physiological';

  const { data: categoryConfigs } = useGetCategoryConfigs(
    isQuestionnaireRule ? selectedQuestionnaireId : null,
  );
  const { data: journalDetail } = useJournal(
    isJournalResponseRule && selectedJournalId ? selectedJournalId : 0,
  );

  const form = useForm<ConfigFormValues>({
    initialValues: defaultConfigValues,
    validate: {
      healthcareFacilityID: (value) =>
        value === null || value === undefined
          ? t('alertRule.form.facilityRequired')
          : null,
      sourceEntityID: (value) => {
        if (!(isQuestionnaireOverdueRule || isQuestionnaireAvailableRule))
          return null;
        return value === null || value === undefined
          ? t('alertRule.form.sourceEntityRequired')
          : null;
      },
      conditionJson: (value) => {
        if (
          isQuestionnaireRule ||
          isJournalResponseRule ||
          isJournalInactivityRule ||
          isQuestionnaireOverdueRule ||
          isQuestionnaireAvailableRule
        )
          return null;
        try {
          JSON.parse(value);
          return null;
        } catch {
          return t('alertRule.form.invalidJson');
        }
      },
      conditionCategory: (value) => {
        if (!isQuestionnaireRule) return null;
        return !value ? t('alertRule.form.categoryRequired') : null;
      },
      conditionQuestionId: (value) => {
        if (!isJournalResponseRule) return null;
        return !value ? t('alertRule.form.questionRequired') : null;
      },
      conditionThreshold: (value, values) => {
        if (isQuestionnaireRule && values.conditionEvaluationMode === 'trend')
          return null;
        if (isQuestionnaireRule || isPhysiologicalRule)
          {return value === '' || value === null || value === undefined
            ? t('alertRule.form.valueRequired')
            : null;}

        if (isJournalResponseRule) {
          if (values.conditionEvaluationMode === 'trend') return null;
          const question = getQuestionType(
            form.values.conditionQuestionId,
            journalDetail?.categories,
          );
          const isNumeric =
            question != null &&
            [
              QUESTION_TYPE_IDS.YES_NO_WITH_SCALE,
              QUESTION_TYPE_IDS.YES_NO_WITH_COUNT,
              QUESTION_TYPE_IDS.SCALE,
              QUESTION_TYPE_IDS.YES_NO_WITH_NUMERIC_SCALE,
            ].includes(question.questionTypeID);
          if (isNumeric)
            {return value === '' || value === null || value === undefined
              ? t('alertRule.form.valueRequired')
              : null;}
        }
        return null;
      },
      conditionTriggerValues: (value) => {
        if (isJournalResponseRule) {
          const question = getQuestionType(
            form.values.conditionQuestionId,
            journalDetail?.categories,
          );
          const isSelect =
            question != null &&
            [
              QUESTION_TYPE_IDS.YES_NO,
              QUESTION_TYPE_IDS.MULTI_SELECT,
              QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT,
            ].includes(question.questionTypeID);
          if (isSelect)
            {return !value || value.length === 0
              ? t('alertRule.form.triggerValuesRequired')
              : null;}
        }
        return null;
      },
      conditionKeywords: (value) => {
        if (isJournalResponseRule) {
          const question = getQuestionType(
            form.values.conditionQuestionId,
            journalDetail?.categories,
          );
          const isText =
            question != null &&
            question.questionTypeID === QUESTION_TYPE_IDS.FREE_TEXT;
          if (isText)
            {return !value || value.length === 0
              ? t('alertRule.form.keywordsRequired')
              : null;}
        }
        return null;
      },
      severity: (value) =>
        !value ? t('alertRule.form.severityRequired') : null,
      notificationHcpID: (value, values) => {
        if (!values.notificationEnabled) return null;
        return !value ? t('alertRule.form.notificationTargetRequired') : null;
      },
      notifyByEmail: (_value, values) => {
        if (!values.notificationEnabled || !values.notificationHcpID)
          return null;
        return !values.notifyByEmail && !values.notifyBySMS
          ? t('alertRule.form.atLeastOneChannelRequired')
          : null;
      },
      escalationDelayMinutes: (value, values) => {
        if (!values.escalationEnabled) return null;
        return !value || value <= 0
          ? t('alertRule.form.escalationDelayRequired')
          : null;
      },
      escalationTargetID: (value, values) => {
        if (!values.escalationEnabled) return null;
        return !value ? t('alertRule.form.escalationTargetRequired') : null;
      },
      escalationNotifyByEmail: (_value, values) => {
        if (!values.escalationEnabled || !values.escalationTargetID)
          return null;
        return !values.escalationNotifyByEmail && !values.escalationNotifyBySMS
          ? t('alertRule.form.atLeastOneChannelRequired')
          : null;
      },
      escalationEmailRecipients: (value, values) => {
        if (!values.escalationEnabled || !value) return null;
        const emails = value
          .split(';')
          .map((e: string) => e.trim())
          .filter(Boolean);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalid = emails.filter((e: string) => !emailRegex.test(e));
        return invalid.length > 0
          ? t('alertRule.form.escalationEmailRecipientsInvalid', {
              emails: invalid.join(', '),
            })
          : null;
      },
      escalationSmsRecipients: (value, values) => {
        if (!values.escalationEnabled || !value) return null;
        const phones = value
          .split(';')
          .map((p: string) => p.trim())
          .filter(Boolean);
        const usPhoneRegex = /^\(\d{3}\)\s?\d{3}-\d{4}$/;
        const invalid = phones.filter((p: string) => !usPhoneRegex.test(p));
        return invalid.length > 0
          ? t('alertRule.form.escalationSmsRecipientsInvalid', {
              phones: invalid.join(', '),
            })
          : null;
      },
      level2EscalationDelayMinutes: (value, values) => {
        if (!values.level2EscalationEnabled) return null;
        return !value || value <= 0
          ? t('alertRule.form.level2EscalationDelayRequired')
          : null;
      },
      level2EscalationTargetID: (value, values) => {
        if (!values.level2EscalationEnabled) return null;
        return !value
          ? t('alertRule.form.level2EscalationTargetRequired')
          : null;
      },
      level2EscalationNotifyByEmail: (_value, values) => {
        if (!values.level2EscalationEnabled || !values.level2EscalationTargetID)
          return null;
        return !values.level2EscalationNotifyByEmail &&
          !values.level2EscalationNotifyBySMS
          ? t('alertRule.form.atLeastOneChannelRequired')
          : null;
      },
      level2EscalationEmailRecipients: (value, values) => {
        if (!values.level2EscalationEnabled || !value) return null;
        const emails = value
          .split(';')
          .map((e: string) => e.trim())
          .filter(Boolean);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalid = emails.filter((e: string) => !emailRegex.test(e));
        return invalid.length > 0
          ? t('alertRule.form.escalationEmailRecipientsInvalid', {
              emails: invalid.join(', '),
            })
          : null;
      },
      level2EscalationSmsRecipients: (value, values) => {
        if (!values.level2EscalationEnabled || !value) return null;
        const phones = value
          .split(';')
          .map((p: string) => p.trim())
          .filter(Boolean);
        const usPhoneRegex = /^\(\d{3}\)\s?\d{3}-\d{4}$/;
        const invalid = phones.filter((p: string) => !usPhoneRegex.test(p));
        return invalid.length > 0
          ? t('alertRule.form.escalationSmsRecipientsInvalid', {
              phones: invalid.join(', '),
            })
          : null;
      },
    },
  });

  const facilityOptions = useMemo(
    () => facilities.map((f) => ({ value: f.id.toString(), label: f.name })),
    [facilities],
  );

  const selectedFacility = useMemo(
    () =>
      facilities.find((f) => f.id === form.values.healthcareFacilityID) ?? null,
    [facilities, form.values.healthcareFacilityID],
  );

  const sourceEntityOptions = useMemo(() => {
    switch (rule.sourceType) {
      case 'Journal': {
        if (selectedFacility) {
          return journals
            .filter((j) =>
              selectedFacility.therapeuticIndicationIds?.includes(
                j.therapeuticIndicationID,
              ),
            )
            .map((j) => ({ value: j.id.toString(), label: j.name }));
        }
        return journals.map((j) => ({ value: j.id.toString(), label: j.name }));
      }
      case 'Questionnaire': {
        if (selectedFacility) {
          return questionnaires
            .filter((q) => selectedFacility.questionnaireIds?.includes(q.id))
            .map((q) => ({ value: q.id.toString(), label: q.name }));
        }
        return questionnaires.map((q) => ({
          value: q.id.toString(),
          label: q.name,
        }));
      }
      case 'Physiological': {
        const seenIds = new Set<number>();
        return physiologicalTypes
          .filter((p) => {
            if (seenIds.has(p.id)) return false;
            seenIds.add(p.id);
            return true;
          })
          .map((p) => ({ value: p.id.toString(), label: p.name }));
      }
      default:
        return [];
    }
  }, [rule, journals, questionnaires, physiologicalTypes, selectedFacility]);

  const categoryOptions = useMemo(
    () =>
      (categoryConfigs || []).map((c) => ({
        value: c.categoryEn,
        label: isEn ? c.categoryEn : c.categoryFr,
      })),
    [categoryConfigs, isEn],
  );

  const selectedJournalQuestion = useMemo(
    () =>
      getQuestionType(
        form.values.conditionQuestionId,
        journalDetail?.categories,
      ),
    [form.values.conditionQuestionId, journalDetail],
  );
  const isJournalNumericQuestion =
    selectedJournalQuestion != null &&
    [
      QUESTION_TYPE_IDS.YES_NO_WITH_SCALE,
      QUESTION_TYPE_IDS.YES_NO_WITH_COUNT,
      QUESTION_TYPE_IDS.SCALE,
      QUESTION_TYPE_IDS.YES_NO_WITH_NUMERIC_SCALE,
    ].includes(selectedJournalQuestion.questionTypeID);

  const showTrendToggle =
    isQuestionnaireRule ||
    isPhysiologicalRule ||
    (isJournalResponseRule && isJournalNumericQuestion);

  const questionOptions = useMemo(() => {
    if (!journalDetail?.categories) return [];
    return journalDetail.categories.map((c) => ({
      group: isEn ? c.nameEn : c.nameFr,
      items: (c.questions || []).map((q) => ({
        value: q.id.toString(),
        label: isEn ? q.questionTextEn : q.questionTextFr,
      })),
    }));
  }, [journalDetail, isEn]);

  const physiologicalUnits = useMemo(() => {
    if (!isPhysiologicalRule || !form.values.sourceEntityID) return [];
    const entries = physiologicalTypes.filter(
      (p) => p.id === form.values.sourceEntityID,
    );
    const seenUnits = new Set<number>();
    return entries
      .filter((p) => {
        if (seenUnits.has(p.unitID)) return false;
        seenUnits.add(p.unitID);
        return true;
      })
      .map((p) => ({ value: p.unitID.toString(), label: p.unitName }));
  }, [isPhysiologicalRule, form.values.sourceEntityID, physiologicalTypes]);

  const hcpOptions = useMemo(() => {
    if (!facilityHcps) return [];
    return facilityHcps.map((hcp) => ({
      value: hcp.id.toString(),
      label: hcp.name || `${hcp.firstName} ${hcp.lastName}`,
    }));
  }, [facilityHcps]);

  const hcpHasPhone = (hcpId: number | null) => {
    if (!facilityHcps || !hcpId) return false;
    const hcp = facilityHcps.find((h) => h.id === hcpId);
    return !!hcp?.phoneNumber;
  };

  const getEscalationDelayDisplay = (minutes: number | null): number => {
    if (!minutes) return 0;
    if (escalationDelayUnit === 'days') return minutes / 1440;
    if (escalationDelayUnit === 'hours') return minutes / 60;
    return minutes;
  };

  const getLevel2EscalationDelayDisplay = (minutes: number | null): number => {
    if (!minutes) return 0;
    if (level2EscalationDelayUnit === 'days') return minutes / 1440;
    if (level2EscalationDelayUnit === 'hours') return minutes / 60;
    return minutes;
  };

  useEffect(() => {
    if (!opened) return;

    if (!editingConfig) {
      setSelectedQuestionnaireId(null);
      setSelectedJournalId(null);
      setSelectedFacilityId(null);
      setEscalationDelayUnit('days');
      setLevel2EscalationDelayUnit('days');
      form.setValues(defaultConfigValues);
      return;
    }

    setSelectedFacilityId(editingConfig.healthcareFacilityID ?? null);

    if (
      editingConfig.escalationDelayMinutes &&
      editingConfig.escalationDelayMinutes % 1440 === 0
    )
      setEscalationDelayUnit('days');
    else if (
      editingConfig.escalationDelayMinutes &&
      editingConfig.escalationDelayMinutes % 60 === 0
    )
      setEscalationDelayUnit('hours');
    else setEscalationDelayUnit('minutes');

    if (
      editingConfig.level2EscalationDelayMinutes &&
      editingConfig.level2EscalationDelayMinutes % 1440 === 0
    )
      setLevel2EscalationDelayUnit('days');
    else if (
      editingConfig.level2EscalationDelayMinutes &&
      editingConfig.level2EscalationDelayMinutes % 60 === 0
    )
      setLevel2EscalationDelayUnit('hours');
    else setLevel2EscalationDelayUnit('minutes');

    let conditionCategory: string | null = null;
    let conditionQuestionId: string | null = null;
    let conditionOperator = '>=';
    let conditionThreshold: number | string = '';
    let conditionUnitId: number | null = null;
    let conditionTriggerValues: string[] = [];
    let conditionKeywords: string[] = [];
    let conditionEvaluationMode = 'threshold';
    let trendType = TREND_TYPE_SUDDEN_INCREASE;
    let trendDeltaThreshold = 3;
    let trendMinConsecutiveIncreases = 2;
    let trendMeanDeltaThreshold = 2;
    let trendWindowSize = 3;
    let trendMinCurrentScore = 8;
    let trendMissingDataPolicy = 'skip';

    if (isQuestionnaireRule && editingConfig.conditionJson) {
      try {
        const parsed = JSON.parse(editingConfig.conditionJson);
        conditionCategory = parsed.categoryEn || null;
        conditionEvaluationMode = parsed.evaluationMode || 'threshold';

        if (conditionEvaluationMode === 'trend') {
          trendType = parsed.trendType || TREND_TYPE_SUDDEN_INCREASE;
          trendMissingDataPolicy = parsed.missingDataPolicy || 'skip';
          trendDeltaThreshold = parsed.deltaThreshold ?? 3;
          trendMinConsecutiveIncreases = parsed.minConsecutiveIncreases ?? 2;
          trendMeanDeltaThreshold = parsed.meanDeltaThreshold ?? 2;
          trendWindowSize = parsed.windowSize ?? 3;
          trendMinCurrentScore = parsed.minCurrentScore ?? 8;
        } else {
          conditionOperator = parsed.operator || '>=';
          conditionThreshold = parsed.threshold ?? '';
        }
      } catch {
        /* keep defaults */
      }
    }

    if (isPhysiologicalRule && editingConfig.conditionJson) {
      try {
        const parsed = JSON.parse(editingConfig.conditionJson);
        conditionOperator = parsed.operator || '>=';
        conditionThreshold = parsed.threshold ?? '';
        conditionUnitId = parsed.unitId ?? null;
      } catch {
        /* keep defaults */
      }
    }

    if (isJournalResponseRule && editingConfig.conditionJson) {
      try {
        const parsed = JSON.parse(editingConfig.conditionJson);
        conditionQuestionId = parsed.journalQuestionId?.toString() || null;
        conditionEvaluationMode = parsed.evaluationMode || 'threshold';

        if (conditionEvaluationMode === 'trend') {
          trendType = parsed.trendType || TREND_TYPE_SUDDEN_INCREASE;
          trendMissingDataPolicy = parsed.missingDataPolicy || 'skip';
          trendDeltaThreshold = parsed.deltaThreshold ?? 3;
          trendMinConsecutiveIncreases = parsed.minConsecutiveIncreases ?? 2;
          trendMeanDeltaThreshold = parsed.meanDeltaThreshold ?? 2;
          trendWindowSize = parsed.windowSize ?? 3;
          trendMinCurrentScore = parsed.minCurrentScore ?? 8;
        } else {
          conditionOperator = parsed.operator || '>=';
          conditionThreshold = parsed.numericThreshold ?? '';
          conditionTriggerValues = parsed.triggerValues || [];
          conditionKeywords = parsed.keywords || [];
        }
      } catch {
        /* keep defaults */
      }
    }

    let conditionThresholdDays = 3;
    let conditionMaxReminders = 3;

    if (
      (isJournalInactivityRule || isQuestionnaireOverdueRule) &&
      editingConfig.conditionJson
    ) {
      try {
        const parsed = JSON.parse(editingConfig.conditionJson);
        conditionThresholdDays = parsed.thresholdDays ?? 3;
        conditionMaxReminders = parsed.maxReminders ?? 3;
      } catch {
        /* keep defaults */
      }
    }

    if (
      (isQuestionnaireRule ||
        isQuestionnaireOverdueRule ||
        isQuestionnaireAvailableRule) &&
      editingConfig.sourceEntityID
    )
      setSelectedQuestionnaireId(editingConfig.sourceEntityID);

    if (isJournalResponseRule && editingConfig.sourceEntityID)
      setSelectedJournalId(editingConfig.sourceEntityID);

    form.setValues({
      conditionJson: editingConfig.conditionJson || '{}',
      conditionCategory,
      conditionQuestionId,
      conditionOperator,
      conditionThreshold,
      conditionTriggerValues,
      conditionKeywords,
      conditionUnitId,
      conditionThresholdDays,
      conditionMaxReminders,
      conditionEvaluationMode,
      trendType,
      trendDeltaThreshold,
      trendMinConsecutiveIncreases,
      trendMeanDeltaThreshold,
      trendWindowSize,
      trendMinCurrentScore,
      trendMissingDataPolicy,
      conditionTrendType: null,
      conditionSampleCount: 3,
      conditionTrendValue: '',
      conditionValueMode: 'absolute',
      severity: editingConfig.severity,
      deliveryChannels: editingConfig.deliveryChannels || 'InApp',
      escalationEnabled: editingConfig.escalationEnabled,
      escalationDelayMinutes: editingConfig.escalationDelayMinutes,
      escalationTargetType: editingConfig.escalationTargetType || '',
      escalationTargetID: editingConfig.escalationTargetID,
      escalationEmailRecipients: editingConfig.escalationEmailRecipients || '',
      escalationSmsRecipients: editingConfig.escalationSmsRecipients || '',
      level2EscalationEnabled: editingConfig.level2EscalationEnabled,
      level2EscalationDelayMinutes: editingConfig.level2EscalationDelayMinutes,
      level2EscalationTargetType:
        editingConfig.level2EscalationTargetType || '',
      level2EscalationTargetID: editingConfig.level2EscalationTargetID,
      level2EscalationEmailRecipients:
        editingConfig.level2EscalationEmailRecipients || '',
      level2EscalationSmsRecipients:
        editingConfig.level2EscalationSmsRecipients || '',
      isEnabled: editingConfig.isEnabled,
      healthcareFacilityID: editingConfig.healthcareFacilityID,
      sourceEntityID: editingConfig.sourceEntityID,
      notificationEnabled: editingConfig.notificationEnabled,
      notificationHcpID: editingConfig.notificationHcpID,
      notifyByEmail: editingConfig.notifyByEmail,
      notifyBySMS: editingConfig.notifyBySMS,
      escalationNotifyByEmail: editingConfig.escalationNotifyByEmail,
      escalationNotifyBySMS: editingConfig.escalationNotifyBySMS,
      level2EscalationNotifyByEmail:
        editingConfig.level2EscalationNotifyByEmail,
      level2EscalationNotifyBySMS: editingConfig.level2EscalationNotifyBySMS,
      notifyPatientBySMS: editingConfig.notifyPatientBySMS,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setValues must not be a dep (infinite loop)
  }, [
    opened,
    editingConfig,
    isQuestionnaireRule,
    isPhysiologicalRule,
    isJournalResponseRule,
    isJournalInactivityRule,
  ]);

  const handleSubmit = (values: ConfigFormValues) => {
    let conditionJson = values.conditionJson;

    if (isQuestionnaireRule) {
      if (values.conditionEvaluationMode === 'trend') {
        conditionJson = JSON.stringify({
          evaluationMode: 'trend',
          categoryEn: values.conditionCategory,
          trendType: values.trendType,
          missingDataPolicy: values.trendMissingDataPolicy,
          deltaThreshold: values.trendDeltaThreshold,
          minConsecutiveIncreases: values.trendMinConsecutiveIncreases,
          meanDeltaThreshold: values.trendMeanDeltaThreshold,
          windowSize: values.trendWindowSize,
          minCurrentScore: values.trendMinCurrentScore,
        });
      } else {
        conditionJson = JSON.stringify({
          evaluationMode: 'threshold',
          categoryEn: values.conditionCategory,
          operator: values.conditionOperator,
          threshold: Number(values.conditionThreshold),
        });
      }
    } else if (isPhysiologicalRule) {
      const unit =
        physiologicalUnits.length === 1
          ? physiologicalUnits[0]
          : physiologicalUnits.find(
              (u) => u.value === values.conditionUnitId?.toString(),
            );
      conditionJson = JSON.stringify({
        physiologicalTypeId: values.sourceEntityID,
        operator: values.conditionOperator,
        threshold: Number(values.conditionThreshold),
        unitId: unit ? Number(unit.value) : null,
        unitName: unit?.label || '',
      });
    } else if (isJournalInactivityRule || isQuestionnaireOverdueRule) {
      conditionJson = JSON.stringify({
        thresholdDays: Number(values.conditionThresholdDays),
        maxReminders: Number(values.conditionMaxReminders),
      });
    } else if (isQuestionnaireAvailableRule) {
      // Availability reminder fires once when the questionnaire becomes available;
      // it has no numeric condition parameters.
      conditionJson = '{}';
    } else if (isJournalResponseRule) {
      const question = journalDetail?.categories
        ?.flatMap((c) => c.questions)
        .find((q) => q.id.toString() === values.conditionQuestionId);
      const base = {
        journalQuestionId: Number(values.conditionQuestionId),
        questionTextEn: question?.questionTextEn || '',
        questionTextFr: question?.questionTextFr || '',
      };
      const questionType = getQuestionType(
        values.conditionQuestionId,
        journalDetail?.categories,
      );
      const isNumeric =
        questionType != null &&
        [
          QUESTION_TYPE_IDS.YES_NO_WITH_SCALE,
          QUESTION_TYPE_IDS.YES_NO_WITH_COUNT,
          QUESTION_TYPE_IDS.SCALE,
          QUESTION_TYPE_IDS.YES_NO_WITH_NUMERIC_SCALE,
        ].includes(questionType.questionTypeID);
      const isSelect =
        questionType != null &&
        [
          QUESTION_TYPE_IDS.YES_NO,
          QUESTION_TYPE_IDS.MULTI_SELECT,
          QUESTION_TYPE_IDS.CONDITIONAL_MULTISELECT,
        ].includes(questionType.questionTypeID);
      const isText =
        questionType != null &&
        questionType.questionTypeID === QUESTION_TYPE_IDS.FREE_TEXT;

      if (isNumeric && values.conditionEvaluationMode === 'trend') {
        conditionJson = JSON.stringify({
          ...base,
          evaluationMode: 'trend',
          trendType: values.trendType,
          missingDataPolicy: values.trendMissingDataPolicy,
          deltaThreshold: values.trendDeltaThreshold,
          minConsecutiveIncreases: values.trendMinConsecutiveIncreases,
          meanDeltaThreshold: values.trendMeanDeltaThreshold,
          windowSize: values.trendWindowSize,
          minCurrentScore: values.trendMinCurrentScore,
        });
      } else if (isNumeric) {
        conditionJson = JSON.stringify({
          ...base,
          evaluationMode: 'threshold',
          operator: values.conditionOperator,
          numericThreshold: Number(values.conditionThreshold),
        });
      } else if (isSelect) {
        conditionJson = JSON.stringify({
          ...base,
          triggerValues: values.conditionTriggerValues,
        });
      } else if (isText) {
        conditionJson = JSON.stringify({
          ...base,
          keywords: values.conditionKeywords,
        });
      } else conditionJson = JSON.stringify(base);
    }

    if (editingConfig) {
      const request: IUpdateAlertRuleConfigurationRequest = {
        sourceEntityID: values.sourceEntityID,
        conditionJson,
        severity: values.severity,
        deliveryChannels: values.deliveryChannels,
        escalationEnabled: values.escalationEnabled,
        escalationDelayMinutes: values.escalationDelayMinutes,
        escalationTargetType: values.escalationTargetType || null,
        escalationTargetID: values.escalationTargetID,
        escalationEmailRecipients: values.escalationEmailRecipients || null,
        escalationSmsRecipients: values.escalationSmsRecipients || null,
        level2EscalationEnabled: values.level2EscalationEnabled,
        level2EscalationDelayMinutes: values.level2EscalationDelayMinutes,
        level2EscalationTargetType: values.level2EscalationTargetType || null,
        level2EscalationTargetID: values.level2EscalationTargetID,
        level2EscalationEmailRecipients:
          values.level2EscalationEmailRecipients || null,
        level2EscalationSmsRecipients:
          values.level2EscalationSmsRecipients || null,
        healthcareFacilityID: values.healthcareFacilityID,
        isEnabled: values.isEnabled,
        notificationEnabled: values.notificationEnabled,
        notificationHcpID: values.notificationHcpID,
        notifyByEmail: values.notifyByEmail,
        notifyBySMS: values.notifyBySMS,
        escalationNotifyByEmail: values.escalationNotifyByEmail,
        escalationNotifyBySMS: values.escalationNotifyBySMS,
        level2EscalationNotifyByEmail: values.level2EscalationNotifyByEmail,
        level2EscalationNotifyBySMS: values.level2EscalationNotifyBySMS,
        notifyPatientBySMS: values.notifyPatientBySMS,
      };
      updateConfiguration.mutate(
        { configId: editingConfig.id, request },
        { onSuccess: () => onClose() },
      );
    } else {
      const request: ICreateAlertRuleConfigurationRequest = {
        healthcareFacilityID: values.healthcareFacilityID,
        sourceEntityID: values.sourceEntityID,
        conditionJson,
        severity: values.severity,
        deliveryChannels: values.deliveryChannels,
        escalationEnabled: values.escalationEnabled,
        escalationDelayMinutes: values.escalationDelayMinutes,
        escalationTargetType: values.escalationTargetType || null,
        escalationTargetID: values.escalationTargetID,
        escalationEmailRecipients: values.escalationEmailRecipients || null,
        escalationSmsRecipients: values.escalationSmsRecipients || null,
        level2EscalationEnabled: values.level2EscalationEnabled,
        level2EscalationDelayMinutes: values.level2EscalationDelayMinutes,
        level2EscalationTargetType: values.level2EscalationTargetType || null,
        level2EscalationTargetID: values.level2EscalationTargetID,
        level2EscalationEmailRecipients:
          values.level2EscalationEmailRecipients || null,
        level2EscalationSmsRecipients:
          values.level2EscalationSmsRecipients || null,
        isEnabled: values.isEnabled,
        notificationEnabled: values.notificationEnabled,
        notificationHcpID: values.notificationHcpID,
        notifyByEmail: values.notifyByEmail,
        notifyBySMS: values.notifyBySMS,
        escalationNotifyByEmail: values.escalationNotifyByEmail,
        escalationNotifyBySMS: values.escalationNotifyBySMS,
        level2EscalationNotifyByEmail: values.level2EscalationNotifyByEmail,
        level2EscalationNotifyBySMS: values.level2EscalationNotifyBySMS,
        notifyPatientBySMS: values.notifyPatientBySMS,
      };
      createConfiguration.mutate(
        { alertRuleId: ruleId, request },
        { onSuccess: () => onClose() },
      );
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        editingConfig
          ? t('alertRule.editConfiguration')
          : t('alertRule.addConfiguration')
      }
      size="lg"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Select
            label={t('alertRule.form.facilityId')}
            placeholder={t('alertRule.form.facilityIdPlaceholder')}
            data={facilityOptions}
            searchable
            clearable
            withAsterisk
            error={form.errors.healthcareFacilityID}
            value={form.values.healthcareFacilityID?.toString() ?? null}
            onChange={(val) => {
              const numVal = val ? Number(val) : null;
              form.setFieldValue('healthcareFacilityID', numVal);
              form.clearFieldError('healthcareFacilityID');
              form.setFieldValue('sourceEntityID', null);
              form.setFieldValue('escalationTargetID', null);
              form.setFieldValue('escalationTargetType', '');
              form.setFieldValue('level2EscalationTargetID', null);
              form.setFieldValue('level2EscalationTargetType', '');
              setSelectedFacilityId(numVal);
            }}
          />
          <Select
            label={t('alertRule.form.sourceEntityId')}
            placeholder={t('alertRule.form.sourceEntityIdPlaceholder')}
            data={sourceEntityOptions}
            searchable
            clearable
            withAsterisk={
              isQuestionnaireOverdueRule || isQuestionnaireAvailableRule
            }
            error={form.errors.sourceEntityID}
            value={form.values.sourceEntityID?.toString() ?? null}
            onChange={(val) => {
              const numVal = val ? Number(val) : null;
              form.setFieldValue('sourceEntityID', numVal);
              form.clearFieldError('sourceEntityID');
              if (isQuestionnaireRule) {
                setSelectedQuestionnaireId(numVal);
                form.setFieldValue('conditionCategory', null);
              }
              if (isJournalResponseRule) {
                setSelectedJournalId(numVal);
                form.setFieldValue('conditionQuestionId', null);
                form.setFieldValue('conditionTriggerValues', []);
                form.setFieldValue('conditionKeywords', []);
              }
              if (isPhysiologicalRule)
                form.setFieldValue('conditionUnitId', null);
            }}
          />

          {showTrendToggle && (
            <>
              <Text size="sm" fw={500}>
                {t('alertRule.form.evaluationModeLabel')}
              </Text>
              <SegmentedControl
                data={[
                  {
                    value: 'threshold',
                    label: t('alertRule.form.modeThreshold'),
                  },
                  { value: 'trend', label: t('alertRule.form.modeTrend') },
                ]}
                value={form.values.conditionEvaluationMode}
                onChange={(val) =>
                  form.setFieldValue('conditionEvaluationMode', val)
                }
              />
            </>
          )}

          {isQuestionnaireOverdueRule ? (
            <QuestionnaireOverdueConditionFields form={form} />
          ) : isQuestionnaireAvailableRule ? (
            <Text size="sm" c="dimmed">
              {t('questionnaireAvailabilityReminder.info')}
            </Text>
          ) : isQuestionnaireRule ? (
            <QuestionnaireConditionFields
              form={form}
              categoryOptions={categoryOptions}
              evaluationMode={form.values.conditionEvaluationMode}
            />
          ) : isJournalInactivityRule ? (
            <JournalInactivityConditionFields form={form} />
          ) : isJournalResponseRule ? (
            <JournalConditionFields
              form={form}
              questionOptions={questionOptions}
              categories={journalDetail?.categories}
              isEn={isEn}
              evaluationMode={form.values.conditionEvaluationMode}
            />
          ) : isPhysiologicalRule ? (
            <PhysiologicalConditionFields
              form={form}
              physiologicalUnits={physiologicalUnits}
            />
          ) : (
            <JsonInput
              label={t('alertRule.form.conditionJson')}
              placeholder='{"thresholdDays": 3}'
              formatOnBlur
              autosize
              minRows={3}
              {...form.getInputProps('conditionJson')}
            />
          )}

          <Select
            label={t('alertRule.form.severity')}
            data={getSeverityOptions(t)}
            {...form.getInputProps('severity')}
          />

          <Divider />
          <Switch
            label={t('alertRule.form.notificationEnabled')}
            checked={form.values.notificationEnabled}
            onChange={(e) => {
              const checked = e.currentTarget.checked;
              form.setFieldValue('notificationEnabled', checked);
              if (!checked) {
                form.setFieldValue('notificationHcpID', null);
                form.setFieldValue('notifyByEmail', false);
                form.setFieldValue('notifyBySMS', false);
              }
            }}
          />
          {form.values.notificationEnabled && (
            <>
              <Text size="xs" c="dimmed">
                {t('alertRule.form.notificationDescription')}
              </Text>
              <Group align="flex-end">
                <Select
                  label={t('alertRule.form.notificationTarget')}
                  placeholder={t(
                    'alertRule.form.notificationTargetPlaceholder',
                  )}
                  data={hcpOptions}
                  searchable
                  clearable
                  style={{ flex: 1 }}
                  value={form.values.notificationHcpID?.toString() ?? null}
                  onChange={(val) => {
                    form.setFieldValue(
                      'notificationHcpID',
                      val ? Number(val) : null,
                    );
                    if (!val) {
                      form.setFieldValue('notifyByEmail', false);
                      form.setFieldValue('notifyBySMS', false);
                    }
                  }}
                  error={form.errors.notificationHcpID}
                />
                <Checkbox
                  label={t('alertRule.form.notifyByEmail')}
                  checked={form.values.notifyByEmail}
                  disabled={!form.values.notificationHcpID}
                  onChange={(e) =>
                    form.setFieldValue('notifyByEmail', e.currentTarget.checked)
                  }
                  mt="auto"
                  mb={4}
                />
                <Checkbox
                  label={t('alertRule.form.notifyBySMS')}
                  checked={form.values.notifyBySMS}
                  disabled={
                    !form.values.notificationHcpID ||
                    !hcpHasPhone(form.values.notificationHcpID)
                  }
                  onChange={(e) =>
                    form.setFieldValue('notifyBySMS', e.currentTarget.checked)
                  }
                  mt="auto"
                  mb={4}
                />
              </Group>
              {form.errors.notifyByEmail && (
                <Text size="xs" c="red">
                  {form.errors.notifyByEmail}
                </Text>
              )}
              {form.values.notificationHcpID &&
                !hcpHasPhone(form.values.notificationHcpID) && (
                  <Text size="xs" c="dimmed">
                    {t('alertRule.form.notifySmsNoPhone')}
                  </Text>
                )}
            </>
          )}

          {(isJournalInactivityRule ||
            isQuestionnaireOverdueRule ||
            isQuestionnaireAvailableRule) && (
            <>
              <Divider />
              <Switch
                label={t('alertRule.form.notifyPatientBySMS')}
                description={t(
                  isJournalInactivityRule
                    ? 'alertRule.form.notifyPatientBySMSDescription'
                    : 'alertRule.form.notifyPatientBySMSDescriptionQuestionnaire',
                )}
                checked={form.values.notifyPatientBySMS}
                onChange={(e) =>
                  form.setFieldValue(
                    'notifyPatientBySMS',
                    e.currentTarget.checked,
                  )
                }
              />
            </>
          )}

          <Divider />
          <Switch
            label={t('alertRule.form.escalationEnabled')}
            checked={form.values.escalationEnabled}
            onChange={(e) => {
              const checked = e.currentTarget.checked;
              form.setFieldValue('escalationEnabled', checked);
              if (checked) {
                if (!form.values.escalationDelayMinutes) {
                  form.setFieldValue('escalationDelayMinutes', 7200);
                  setEscalationDelayUnit('days');
                }
              } else {
                form.setFieldValue('escalationNotifyByEmail', false);
                form.setFieldValue('escalationNotifyBySMS', false);
                form.setFieldValue('level2EscalationEnabled', false);
                form.setFieldValue('level2EscalationDelayMinutes', null);
                form.setFieldValue('level2EscalationTargetType', '');
                form.setFieldValue('level2EscalationTargetID', null);
                form.setFieldValue('level2EscalationNotifyByEmail', false);
                form.setFieldValue('level2EscalationNotifyBySMS', false);
                setLevel2EscalationDelayUnit('days');
              }
            }}
          />
          {form.values.escalationEnabled && (
            <>
              <Text size="xs" c="dimmed">
                {t('alertRule.form.escalationDescription')}
              </Text>
              <Group grow>
                <NumberInput
                  label={t('alertRule.form.escalationDelay')}
                  min={1}
                  value={getEscalationDelayDisplay(
                    form.values.escalationDelayMinutes,
                  )}
                  onChange={(val) => {
                    const num = typeof val === 'number' ? val : 0;
                    const minutes =
                      escalationDelayUnit === 'days'
                        ? num * 1440
                        : escalationDelayUnit === 'hours'
                          ? num * 60
                          : num;
                    form.setFieldValue('escalationDelayMinutes', minutes);
                  }}
                  error={form.errors.escalationDelayMinutes}
                />
                <Select
                  label={t('alertRule.form.escalationUnit')}
                  data={[
                    {
                      value: 'minutes',
                      label: t('alertRule.form.escalationMinutes'),
                    },
                    {
                      value: 'hours',
                      label: t('alertRule.form.escalationHours'),
                    },
                    {
                      value: 'days',
                      label: t('alertRule.form.escalationDays'),
                    },
                  ]}
                  value={escalationDelayUnit}
                  onChange={(val) => {
                    const unit =
                      (val as 'minutes' | 'hours' | 'days') || 'hours';
                    const currentMinutes =
                      form.values.escalationDelayMinutes || 0;
                    const currentValue =
                      escalationDelayUnit === 'days'
                        ? currentMinutes / 1440
                        : escalationDelayUnit === 'hours'
                          ? currentMinutes / 60
                          : currentMinutes;
                    const newMinutes =
                      unit === 'days'
                        ? currentValue * 1440
                        : unit === 'hours'
                          ? currentValue * 60
                          : currentValue;
                    setEscalationDelayUnit(unit);
                    form.setFieldValue(
                      'escalationDelayMinutes',
                      Math.round(newMinutes),
                    );
                  }}
                />
              </Group>
              <Group align="flex-end">
                <Select
                  label={t('alertRule.form.escalationTarget')}
                  placeholder={t('alertRule.form.escalationTargetPlaceholder')}
                  data={hcpOptions}
                  searchable
                  clearable
                  style={{ flex: 1 }}
                  value={form.values.escalationTargetID?.toString() ?? null}
                  onChange={(val) => {
                    form.setFieldValue(
                      'escalationTargetID',
                      val ? Number(val) : null,
                    );
                    form.setFieldValue(
                      'escalationTargetType',
                      val ? 'HCP' : '',
                    );
                    if (!val) {
                      form.setFieldValue('escalationNotifyByEmail', false);
                      form.setFieldValue('escalationNotifyBySMS', false);
                    }
                  }}
                  error={form.errors.escalationTargetID}
                />
                <Checkbox
                  label={t('alertRule.form.notifyByEmail')}
                  checked={form.values.escalationNotifyByEmail}
                  disabled={!form.values.escalationTargetID}
                  onChange={(e) =>
                    form.setFieldValue(
                      'escalationNotifyByEmail',
                      e.currentTarget.checked,
                    )
                  }
                  mt="auto"
                  mb={4}
                />
                <Checkbox
                  label={t('alertRule.form.notifyBySMS')}
                  checked={form.values.escalationNotifyBySMS}
                  disabled={
                    !form.values.escalationTargetID ||
                    !hcpHasPhone(form.values.escalationTargetID)
                  }
                  onChange={(e) =>
                    form.setFieldValue(
                      'escalationNotifyBySMS',
                      e.currentTarget.checked,
                    )
                  }
                  mt="auto"
                  mb={4}
                />
              </Group>
              {form.errors.escalationNotifyByEmail && (
                <Text size="xs" c="red">
                  {form.errors.escalationNotifyByEmail}
                </Text>
              )}
              {form.values.escalationTargetID &&
                !hcpHasPhone(form.values.escalationTargetID) && (
                  <Text size="xs" c="dimmed">
                    {t('alertRule.form.notifySmsNoPhone')}
                  </Text>
                )}
              <TextInput
                label={t('alertRule.form.escalationEmailRecipients')}
                description={t(
                  'alertRule.form.escalationEmailRecipientsDescription',
                )}
                placeholder="email1@example.com; email2@example.com"
                {...form.getInputProps('escalationEmailRecipients')}
              />
              <TextInput
                label={t('alertRule.form.escalationSmsRecipients')}
                description={t(
                  'alertRule.form.escalationSmsRecipientsDescription',
                )}
                placeholder="(415) 555-1212; (514) 555-1212"
                {...form.getInputProps('escalationSmsRecipients')}
                onBlur={(e) =>
                  form.setFieldValue(
                    'escalationSmsRecipients',
                    Utils.formatPhoneNumbers(e.currentTarget.value),
                  )
                }
              />
              <Divider variant="dashed" />
              <Switch
                label={t('alertRule.form.level2EscalationEnabled')}
                checked={form.values.level2EscalationEnabled}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  form.setFieldValue('level2EscalationEnabled', checked);
                  if (checked) {
                    if (!form.values.level2EscalationDelayMinutes) {
                      form.setFieldValue('level2EscalationDelayMinutes', 1440);
                      setLevel2EscalationDelayUnit('days');
                    }
                  } else {
                    form.setFieldValue('level2EscalationDelayMinutes', null);
                    form.setFieldValue('level2EscalationTargetType', '');
                    form.setFieldValue('level2EscalationTargetID', null);
                    form.setFieldValue('level2EscalationNotifyByEmail', false);
                    form.setFieldValue('level2EscalationNotifyBySMS', false);
                    setLevel2EscalationDelayUnit('days');
                  }
                }}
              />
              {form.values.level2EscalationEnabled && (
                <>
                  <Text size="xs" c="dimmed">
                    {t('alertRule.form.level2EscalationDescription')}
                  </Text>
                  <Group grow>
                    <NumberInput
                      label={t('alertRule.form.level2EscalationDelay')}
                      min={1}
                      value={getLevel2EscalationDelayDisplay(
                        form.values.level2EscalationDelayMinutes,
                      )}
                      onChange={(val) => {
                        const num = typeof val === 'number' ? val : 0;
                        const minutes =
                          level2EscalationDelayUnit === 'days'
                            ? num * 1440
                            : level2EscalationDelayUnit === 'hours'
                              ? num * 60
                              : num;
                        form.setFieldValue(
                          'level2EscalationDelayMinutes',
                          minutes,
                        );
                      }}
                      error={form.errors.level2EscalationDelayMinutes}
                    />
                    <Select
                      label={t('alertRule.form.escalationUnit')}
                      data={[
                        {
                          value: 'minutes',
                          label: t('alertRule.form.escalationMinutes'),
                        },
                        {
                          value: 'hours',
                          label: t('alertRule.form.escalationHours'),
                        },
                        {
                          value: 'days',
                          label: t('alertRule.form.escalationDays'),
                        },
                      ]}
                      value={level2EscalationDelayUnit}
                      onChange={(val) => {
                        const unit =
                          (val as 'minutes' | 'hours' | 'days') || 'hours';
                        const currentMinutes =
                          form.values.level2EscalationDelayMinutes || 0;
                        const currentValue =
                          level2EscalationDelayUnit === 'days'
                            ? currentMinutes / 1440
                            : level2EscalationDelayUnit === 'hours'
                              ? currentMinutes / 60
                              : currentMinutes;
                        const newMinutes =
                          unit === 'days'
                            ? currentValue * 1440
                            : unit === 'hours'
                              ? currentValue * 60
                              : currentValue;
                        setLevel2EscalationDelayUnit(unit);
                        form.setFieldValue(
                          'level2EscalationDelayMinutes',
                          Math.round(newMinutes),
                        );
                      }}
                    />
                  </Group>
                  <Group align="flex-end">
                    <Select
                      label={t('alertRule.form.level2EscalationTarget')}
                      placeholder={t(
                        'alertRule.form.level2EscalationTargetPlaceholder',
                      )}
                      data={hcpOptions}
                      searchable
                      clearable
                      style={{ flex: 1 }}
                      value={
                        form.values.level2EscalationTargetID?.toString() ?? null
                      }
                      onChange={(val) => {
                        form.setFieldValue(
                          'level2EscalationTargetID',
                          val ? Number(val) : null,
                        );
                        form.setFieldValue(
                          'level2EscalationTargetType',
                          val ? 'HCP' : '',
                        );
                        if (!val) {
                          form.setFieldValue(
                            'level2EscalationNotifyByEmail',
                            false,
                          );
                          form.setFieldValue(
                            'level2EscalationNotifyBySMS',
                            false,
                          );
                        }
                      }}
                      error={form.errors.level2EscalationTargetID}
                    />
                    <Checkbox
                      label={t('alertRule.form.notifyByEmail')}
                      checked={form.values.level2EscalationNotifyByEmail}
                      disabled={!form.values.level2EscalationTargetID}
                      onChange={(e) =>
                        form.setFieldValue(
                          'level2EscalationNotifyByEmail',
                          e.currentTarget.checked,
                        )
                      }
                      mt="auto"
                      mb={4}
                    />
                    <Checkbox
                      label={t('alertRule.form.notifyBySMS')}
                      checked={form.values.level2EscalationNotifyBySMS}
                      disabled={
                        !form.values.level2EscalationTargetID ||
                        !hcpHasPhone(form.values.level2EscalationTargetID)
                      }
                      onChange={(e) =>
                        form.setFieldValue(
                          'level2EscalationNotifyBySMS',
                          e.currentTarget.checked,
                        )
                      }
                      mt="auto"
                      mb={4}
                    />
                  </Group>
                  {form.errors.level2EscalationNotifyByEmail && (
                    <Text size="xs" c="red">
                      {form.errors.level2EscalationNotifyByEmail}
                    </Text>
                  )}
                  {form.values.level2EscalationTargetID &&
                    !hcpHasPhone(form.values.level2EscalationTargetID) && (
                      <Text size="xs" c="dimmed">
                        {t('alertRule.form.notifySmsNoPhone')}
                      </Text>
                    )}
                  <TextInput
                    label={t('alertRule.form.level2EscalationEmailRecipients')}
                    description={t(
                      'alertRule.form.escalationEmailRecipientsDescription',
                    )}
                    placeholder="email1@example.com; email2@example.com"
                    {...form.getInputProps('level2EscalationEmailRecipients')}
                  />
                  <TextInput
                    label={t('alertRule.form.level2EscalationSmsRecipients')}
                    description={t(
                      'alertRule.form.escalationSmsRecipientsDescription',
                    )}
                    placeholder="(415) 555-1212; (514) 555-1212"
                    {...form.getInputProps('level2EscalationSmsRecipients')}
                    onBlur={(e) =>
                      form.setFieldValue(
                        'level2EscalationSmsRecipients',
                        Utils.formatPhoneNumbers(e.currentTarget.value),
                      )
                    }
                  />
                </>
              )}
            </>
          )}
          <Divider />

          <Checkbox
            label={t('alertRule.form.isEnabled')}
            {...form.getInputProps('isEnabled', { type: 'checkbox' })}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              {t('global.cancel')}
            </Button>
            <Button
              type="submit"
              loading={
                createConfiguration.isPending || updateConfiguration.isPending
              }
            >
              {t('global.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default AlertConfigurationModal;
